import { type FC, type Dispatch, type SetStateAction, useEffect } from "react";

import { RadioGroup, RadioGroupItem } from "@/shared/components/ui/radio-group";
import { Label } from "@/shared/components/ui/label";

import type { FormField } from "@/shared/types/estateEssentials";
import type { EstateItem } from "@/shared/types/estateTypes";

import { resolveFieldLabel } from "../utils/formDisplayUtils";
import { resolveFieldOptions, isFieldHidden } from "../utils/resolveFieldOptions";
import { getVehicleLabel, getVehiclePrefix } from "../utils/vehicleUtils";
import { FieldGrid } from "./FieldGrid";
import { FormFieldInput } from "./FormFieldInput";
import { SectionNumber } from "./SectionHeader";

// --- Vehicle Count Section ---

interface VehicleCountSectionProps {
  sectionIndex: number;
  sectionLabel: string;
  sectionFields: FormField[];
  vehicleItems: EstateItem[];
  formValues: Record<string, string>;
  setFormValues: Dispatch<SetStateAction<Record<string, string>>>;
}

export const VehicleCountSection: FC<VehicleCountSectionProps> = ({
  sectionIndex,
  sectionLabel,
  sectionFields,
  vehicleItems,
  formValues,
  setFormValues,
}) => {
  const selectedId = formValues.selected_vehicle_id;
  const stepperField = sectionFields.find((f) => f.type === "stepper");
  const vehicleCount = Math.max(
    vehicleItems.length,
    parseInt(formValues.vehicle_count ?? String(vehicleItems.length), 10),
  );
  const extraCount = vehicleCount - vehicleItems.length;

  const handleVehicleSelect = (vehicleId: string) => {
    setFormValues((prev) => ({ ...prev, selected_vehicle_id: vehicleId }));
  };

  // Auto-select the last vehicle when none is selected
  useEffect(() => {
    if (selectedId) return;
    const lastId =
      vehicleItems.length > 0
        ? String(vehicleItems[vehicleItems.length - 1].id)
        : extraCount > 0
          ? `new-${extraCount - 1}`
          : undefined;
    if (lastId) {
      setFormValues((prev) => ({ ...prev, selected_vehicle_id: lastId }));
    }
  }, [selectedId, vehicleItems, extraCount, setFormValues]);

  return (
    <div>
      {sectionIndex > 0 && <hr className="border-primary mb-6" />}
      <div className="space-y-4">
        <p className="flex items-center gap-2 text-sm font-medium">
          <SectionNumber n={sectionIndex + 1} />
          {sectionLabel}
        </p>
        {stepperField && (
          <FormFieldInput
            field={stepperField}
            value={formValues.vehicle_count ?? String(vehicleItems.length)}
            onChange={(val) => {
              const num = Math.max(vehicleItems.length, parseInt(val, 10) || vehicleItems.length);
              const prevCount = parseInt(
                formValues.vehicle_count ?? String(vehicleItems.length),
                10,
              );
              const newExtraIndex = num - vehicleItems.length - 1;
              setFormValues((prev) => ({
                ...prev,
                vehicle_count: String(num),
                ...(num > prevCount && newExtraIndex >= 0
                  ? { selected_vehicle_id: `new-${newExtraIndex}` }
                  : {}),
              }));
            }}
          />
        )}
        <div className="space-y-3">
          <p className="text-sm text-secondary">Which vehicle would you like to transfer?</p>
          <RadioGroup
            value={selectedId ?? ""}
            onValueChange={handleVehicleSelect}
            className="flex items-center gap-6"
          >
            {vehicleItems.map((v) => (
              <div key={v.id} className="flex items-center gap-2">
                <RadioGroupItem value={String(v.id)} id={`vehicle-${v.id}`} />
                <Label htmlFor={`vehicle-${v.id}`} className="text-sm font-normal cursor-pointer">
                  {getVehicleLabel(v)}
                </Label>
              </div>
            ))}
            {Array.from({ length: extraCount }, (_, i) => (
              <div key={`new-vehicle-${i}`} className="flex items-center gap-2">
                <RadioGroupItem value={`new-${i}`} id={`vehicle-new-${i}`} />
                <Label htmlFor={`vehicle-new-${i}`} className="text-sm font-normal cursor-pointer">
                  New vehicle {i + 1}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      </div>
    </div>
  );
};

// --- Vehicle Transfer Section ---

interface VehicleTransferSectionProps {
  sectionIndex: number;
  sectionFields: FormField[];
  vehicleItems: EstateItem[];
  hasVehicleItems: boolean;
  formValues: Record<string, string>;
  errors: Record<string, string>;
  onFieldChange: (key: string, value: string) => void;
  attentionKeys: Set<string>;
  relationship?: string;
  deceasedFirstName: string;
  portalContainer?: HTMLElement | null;
}

function buildPrefixedAttention(sectionFields: FormField[], pfx: string): Set<string> {
  const prefixed = new Set<string>();
  for (const f of sectionFields) {
    if (f.required) prefixed.add(`${pfx}${f.key}`);
  }
  return prefixed;
}

function buildPrefixedFields(
  sectionFields: FormField[],
  prefix: string,
  formValues: Record<string, string>,
  deceasedFirstName: string,
): FormField[] {
  return sectionFields
    .map((f) => ({
      ...f,
      key: `${prefix}${f.key}`,
      label: resolveFieldLabel(f, deceasedFirstName),
    }))
    .filter((f) => !isFieldHidden(f, formValues))
    .map((f) => resolveFieldOptions(f, formValues));
}

export const VehicleTransferSection: FC<VehicleTransferSectionProps> = ({
  sectionIndex,
  sectionFields,
  vehicleItems,
  hasVehicleItems,
  formValues,
  errors,
  onFieldChange,
  attentionKeys,
  relationship,
  deceasedFirstName,
  portalContainer,
}) => {
  const vehicleCount = Math.max(1, parseInt(formValues.vehicle_count ?? "1", 10));
  const selectedId = formValues.selected_vehicle_id;

  // Estate vehicles present: render the selected vehicle's fields
  if (hasVehicleItems) {
    if (!selectedId) return null;

    const isNewVehicle = selectedId.startsWith("new-");
    const prefix = getVehiclePrefix(selectedId);
    const prefixedAttention = buildPrefixedAttention(sectionFields, prefix);
    const prefixedFields = buildPrefixedFields(
      sectionFields,
      prefix,
      formValues,
      deceasedFirstName,
    );

    const sectionTitle = isNewVehicle
      ? `Transfer information - New vehicle ${parseInt(selectedId.replace("new-", ""), 10) + 1}`
      : `Transfer information - ${(() => {
          const sv = vehicleItems.find((v) => String(v.id) === selectedId);
          return sv ? getVehicleLabel(sv, "Selected vehicle") : "Selected vehicle";
        })()}`;

    return (
      <div key={`transfer_info_${selectedId}`}>
        <hr className="border-primary mb-6" />
        <div className="space-y-3">
          <p className="flex items-center gap-2 text-sm font-medium">
            <SectionNumber n={sectionIndex + 1} />
            {sectionTitle}
          </p>
          <FieldGrid
            fields={prefixedFields}
            values={formValues}
            errors={errors}
            onFieldChange={onFieldChange}
            relationship={relationship}
            attentionKeys={prefixedAttention}
            deceasedFirstName={deceasedFirstName}
            portalContainer={portalContainer}
          />
        </div>
      </div>
    );
  }

  // No estate vehicles: render N sections with prefixed keys
  return (
    <>
      {Array.from({ length: vehicleCount }, (_, vi) => {
        const prefix = vi === 0 ? "" : `v${vi}_`;
        const viAttention = prefix ? buildPrefixedAttention(sectionFields, prefix) : attentionKeys;
        const resolved = buildPrefixedFields(sectionFields, prefix, formValues, deceasedFirstName);
        const title =
          vehicleCount > 1 ? `Transfer information - Vehicle ${vi + 1}` : "Transfer information";
        return (
          <div key={`transfer_info_${vi}`}>
            <hr className="border-primary mb-6" />
            <div className="space-y-3">
              <p className="flex items-center gap-2 text-sm font-medium">
                <SectionNumber n={sectionIndex + 1 + vi} />
                {title}
              </p>
              <FieldGrid
                fields={resolved}
                values={formValues}
                errors={errors}
                onFieldChange={onFieldChange}
                relationship={relationship}
                attentionKeys={viAttention}
                deceasedFirstName={deceasedFirstName}
                portalContainer={portalContainer}
              />
            </div>
          </div>
        );
      })}
    </>
  );
};
